import { useApp } from "@/context/AppContext";
import { TEMPLATES, DENTAL_STATUS, DENTAL_TONE, FDI, age, live } from "@/lib/clinic";
import { Field, inputCls } from "@/components/ui-kit";

function DentalChart({ value = {}, onChange, readOnly, t }) {
  const cycle = (n) => { if (readOnly) return; const i = DENTAL_STATUS.indexOf(value[n] || "ok"); onChange({ ...value, [n]: DENTAL_STATUS[(i + 1) % DENTAL_STATUS.length] }); };
  return (
    <div className="space-y-2" data-testid="dental-chart" dir="ltr">
      {FDI.map((row, ri) => (
        <div key={ri} className="grid grid-cols-16 gap-1" style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}>
          {row.map((n) => (
            <button key={n} type="button" onClick={() => cycle(n)} title={t(`tooth_${value[n] || "ok"}`)} data-testid={`tooth-${n}`}
              className="h-10 rounded border border-[#999] text-[10px] font-bold flex flex-col items-center justify-center" style={{ background: DENTAL_TONE[value[n] || "ok"], color: (value[n] || "ok") === "ok" ? "#333" : "#fff" }}>{n}</button>
          ))}
        </div>
      ))}
      <div className="flex flex-wrap gap-2 text-xs">{DENTAL_STATUS.map((s) => <span key={s} className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-[#999]" style={{ background: DENTAL_TONE[s] }} />{t(`tooth_${s}`)}</span>)}</div>
    </div>
  );
}

function GrowthChart({ patient, current }) {
  const { view, t } = useApp();
  if (!patient.birth_date) return <p className="text-xs text-muted-foreground">{t("vaccine_need_birth")}</p>;
  const b = new Date(patient.birth_date);
  const pts = [...live(view.encounters).filter((e) => e.patient_id === patient.id && Number(e.vitals?.weight) > 0).map((e) => ({ m: (new Date(e.date) - b) / (30.44 * 86400000), w: Number(e.vitals.weight), h: Number(e.vitals.height) || null })),
    ...(Number(current?.weight) > 0 ? [{ m: (Date.now() - b) / (30.44 * 86400000), w: Number(current.weight), h: Number(current.height) || null, now: true }] : [])].sort((a, c) => a.m - c.m);
  const W = 320, H = 140, maxM = Math.max(24, ...pts.map((p) => p.m)) * 1.05, maxW = Math.max(15, ...pts.map((p) => p.w)) * 1.1;
  const x = (m) => 30 + (m / maxM) * (W - 40), y = (w) => H - 20 - (w / maxW) * (H - 30);
  const median = (m) => (m <= 12 ? 3.3 + m * 0.55 : m <= 60 ? 9.9 + (m - 12) * 0.19 : 19 + (m - 60) * 0.22);
  const ref = Array.from({ length: 25 }, (_, i) => (maxM * i) / 24);
  return (
    <div data-testid="growth-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-md bg-white rounded border border-[#CCCCCC]" dir="ltr">
        <polyline fill="none" stroke="#1F9D55" strokeWidth="1" strokeDasharray="3 3" points={ref.map((m) => `${x(m)},${y(median(m))}`).join(" ")} />
        <polyline fill="none" stroke="#1F9D55" strokeWidth="0.6" strokeDasharray="2 2" points={ref.map((m) => `${x(m)},${y(median(m) * 1.2)}`).join(" ")} />
        <polyline fill="none" stroke="#1F9D55" strokeWidth="0.6" strokeDasharray="2 2" points={ref.map((m) => `${x(m)},${y(median(m) * 0.8)}`).join(" ")} />
        <polyline fill="none" stroke="#2B79C2" strokeWidth="2" points={pts.map((p) => `${x(p.m)},${y(p.w)}`).join(" ")} />
        {pts.map((p, i) => <circle key={i} cx={x(p.m)} cy={y(p.w)} r="3" fill={p.now ? "#C7353B" : "#2B79C2"} />)}
        <line x1="30" y1={H - 20} x2={W - 10} y2={H - 20} stroke="#999" /><line x1="30" y1="10" x2="30" y2={H - 20} stroke="#999" />
        <text x={W - 10} y={H - 6} fontSize="8" textAnchor="end" fill="#666">{t("age")} ({t("months_short")})</text><text x="4" y="14" fontSize="8" fill="#666">kg</text>
      </svg>
      <div className="text-xs text-[#666] mt-1">{t("growth_hint")}</div>
    </div>
  );
}

export default function SpecialtyForm({ specialty, value = {}, onChange, patient, vitals, readOnly }) {
  const { t } = useApp();
  if (!specialty) return null;
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const fields = TEMPLATES[specialty];
  return (
    <section className="rounded border border-[#2B79C2]/40 bg-[#F3F8FD] p-3" data-testid={`specialty-form-${specialty}`}>
      <h3 className="font-bold text-sm mb-2 text-[#1F4E8C]">{t(`sp_${specialty}`)} · {t("specialty_exam")}</h3>
      {specialty === "dentistry" && <DentalChart value={value.teeth || {}} onChange={(teeth) => onChange({ ...value, teeth })} readOnly={readOnly} t={t} />}
      {specialty === "pediatrics" && (
        <div className="grid lg:grid-cols-2 gap-3">
          <GrowthChart patient={patient} current={vitals} />
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("tp_head_circ")}><input dir="ltr" value={value.head_circ || ""} onChange={set("head_circ")} disabled={readOnly} className={`${inputCls} !h-10`} data-testid="tp-head_circ-input" /></Field>
            <Field label={t("tp_feeding")}><input value={value.feeding || ""} onChange={set("feeding")} disabled={readOnly} className={`${inputCls} !h-10`} data-testid="tp-feeding-input" /></Field>
            <Field label={t("tp_development")}><input value={value.development || ""} onChange={set("development")} disabled={readOnly} className={`${inputCls} !h-10`} data-testid="tp-development-input" /></Field>
            <Field label={t("tp_vaccines_ok")}><select value={value.vaccines_ok || ""} onChange={set("vaccines_ok")} disabled={readOnly} className={`${inputCls} !h-10`} data-testid="tp-vaccines_ok-select"><option value="">—</option><option value="yes">{t("yes")}</option><option value="no">{t("no")}</option></select></Field>
          </div>
        </div>
      )}
      {fields && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {fields.map(([k, type, opts]) => (
            <div key={k} className={type === "long" ? "col-span-2 lg:col-span-4" : ""}>
              <Field label={t(`tp_${k}`)}>
                {type === "select" ? <select value={value[k] || ""} onChange={set(k)} disabled={readOnly} className={`${inputCls} !h-10`} data-testid={`tp-${k}-select`}><option value="">—</option>{opts.map((o) => <option key={o} value={o}>{t(`opt_${o}`) === `opt_${o}` ? o : t(`opt_${o}`)}</option>)}</select>
                  : type === "long" ? <textarea rows={2} value={value[k] || ""} onChange={set(k)} disabled={readOnly} className={`${inputCls} h-auto py-2`} data-testid={`tp-${k}-input`} />
                  : <input type={type === "date" ? "date" : "text"} dir="ltr" inputMode={type === "num" ? "decimal" : undefined} value={value[k] || ""} onChange={set(k)} disabled={readOnly} className={`${inputCls} !h-10`} data-testid={`tp-${k}-input`} />}
              </Field>
            </div>
          ))}
        </div>
      )}
      {!fields && !["dentistry", "pediatrics"].includes(specialty) && <p className="text-xs text-muted-foreground">{t("no_template")}</p>}
    </section>
  );
}
